<template>
  <base-modal-component>
    <template v-slot:header>
      <h4 class="u-typography-color md-title">
        Upload images
      </h4>
      <generic-button
        icon
        transparent
        @click="close"
      >
        <md-icon>
          clear
        </md-icon>
      </generic-button>
    </template>

    <template v-slot:body>
      <div class="file-upload">
        <input
          v-model="folderName"
          type="text"
          placeholder="Enter folder name"
          class="folder-input"
        >

        <div
          class="drop-area"
          @dragover.prevent="dragOver"
          @dragleave="dragLeave"
          @drop="dropFiles"
          @click="openFilePicker"
        >
          <div class="drop-text u-typography-color">
            <template v-if="showLoader">
              <md-progress-spinner
                :md-diameter="40"
                md-mode="indeterminate"
                class="spinner-icon"
              />
            </template>
            <template v-else>
              <md-icon class="upload-icon">
                upload_file
              </md-icon>
              <p>Drag & Drop files here or <span class="click-text">click to upload</span></p>
            </template>
          </div>
        </div>

        <input
          ref="fileInput"
          type="file"
          multiple
          accept="image/*"
          class="d-none"
          @change="handleFiles"
        >

        <ul
          v-if="files.length"
          class="file-list"
        >
          <li
            v-for="(file, index) in files"
            :key="index"
          >
            <span class="file-name u-typography-color">{{ file.name }} ({{ (file.size / 1024).toFixed(2) }} KB)</span>
            <button
              class="delete-btn"
              :disabled="showLoader"
              @click="removeFile(index)"
            >
              ❌
            </button>
          </li>
        </ul>
      </div>
    </template>
    <template v-slot:footer>
      <div class="u-mr-xs">
        <generic-button
          :disabled="showLoader"
          variation="grey"
          @click="close"
        >
          Close
        </generic-button>
      </div>
      <div class="u-mr-xs">
        <generic-button
          :disabled="!files.length || showLoader"
          variation="red"
          @click="uploadFiles"
        >
          upload
        </generic-button>
      </div>
    </template>
  </base-modal-component>
</template>

<script>
import BaseModalComponent from '@/shared/modal/BaseModalComponent.vue';
import GenericButton from "@/shared/buttons/GenericButton.vue";
import {configurationsService} from "@/services/configurations";
import {mapActions} from "vuex";

export default {
  name: 'UploadImageModal',
  components: {
    GenericButton,
    BaseModalComponent
  },

  props: {
    currentPathString: {
      type: String,
      required: true
    },
    successFunction: {
      type: Function,
      required: false,
      default: () => {}
    }
  },

  data() {
    return {
      folderName: '',
      files: [],
      acceptedImageTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
      showLoader: false
    };
  },

  methods: {
    ...mapActions("response", ["handleSuccess", "setGlobalError"]),

    openFilePicker() {
      this.$refs.fileInput.click();
    },
    handleFiles(event) {
      const selectedFiles = event.target.files || event.dataTransfer.files;
      if (!selectedFiles.length) return;

      const newFiles = Array.from(selectedFiles);

      const filteredFiles = newFiles.filter(newFile =>
        newFile.type &&
        this.acceptedImageTypes.includes(newFile.type) &&
        !this.files.some(existingFile =>
          existingFile.name === newFile.name && existingFile.size === newFile.size
        )
      );

      if (filteredFiles.length) {
        this.files = [...this.files, ...filteredFiles];
      }
    },
    dropFiles(event) {
      event.preventDefault();
      event.stopPropagation();
      this.handleFiles(event);
    },
    dragOver(event) {
      event.currentTarget.classList.add('dragging');
    },
    dragLeave(event) {
      event.currentTarget.classList.remove('dragging');
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    close() {
      this.$emit('close');
    },
    async uploadFiles() {
      if (!this.files.length) {
        return;
      }

      let fullPath = []

      if (this.currentPathString) {
        fullPath.push(this.currentPathString);
      }

      if (this.folderName) {
        fullPath.push(this.folderName);
      }

      const formData = new FormData();

      formData.append('path', fullPath.join('/'));
      this.files.forEach(file => formData.append("files", file));

      try {
        this.showLoader = true
        await configurationsService.uploadFile(formData)
        this.folderName = ''
        this.files = []
        this.successFunction();
        this.close();

        this.handleSuccess({message: 'Upload successful'})
      } catch (error) {
        this.setGlobalError(error.data.message);
      }

      this.showLoader = false;
    },

  }

};
</script>

<style scoped>
.folder-input {
  width: 100%;
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
}

.file-upload {
  width: 100%;
  margin: auto;
  text-align: center;
}

.drop-area {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #ddd;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.3s;
}

.drop-area:hover,
.drop-area.dragging {
  border-color: #007bff;
  background: rgba(0, 123, 255, 0.1);
}

.drop-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.upload-icon {
  font-size: 48px !important;
}

.click-text {
  color: #007bff;
  font-weight: bold;
  cursor: pointer;
}

.file-list {
  margin-top: 15px;
  padding: 0;
  list-style: none;
}

.file-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
  border: 1px solid #ddd;
  margin-bottom: 5px;
  border-radius: 5px;
}

.file-name {
  flex-grow: 1;
  text-align: left;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.delete-btn {
  background: none;
  border: none;
  color: red;
  cursor: pointer;
  font-size: 16px;
}

.d-none {
  display: none;
}
</style>
